import { DropdownOption } from '@tcl-models/models';
import {
  Institution,
  InstitutionBehavior,
  PrescreeningStates,
  TimePatientArmEvaluationState,
  TimePrescreeningMatchingQueueRequestDtoType,
  TimePrescreeningMatchingSortByDtoType,
  TimePrescreeningMatchingTabCountsResponseDtoType,
  TimePrescreeningSavedSearchResponseDtoType,
  TimePrescreeningScreenedRequestDtoType,
  TimePrescreeningScreenedSortByDtoType,
} from '@tempus/trials-types';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import moment from 'moment';

import {
  GetCareGapMatchingQueueReqBody,
  GetCareGapMatchingTabCountsRes,
  PrescreeningState,
} from '../api/types/nextPrescreening';
import { v3MatchingQueueStates } from '../api/types/timePrescreeningApi';
import {
  BEFORE_SPECIFIED_DATE,
  defaultTrialFilters,
  locationStatusOpts,
  lookbackFilterOpts,
  MatchingTab,
  TableSortValue,
  upcomingVisitFilterTypeOpts,
  upcomingVisitWithinOpts,
  WITHIN,
} from './constants';
import { LocationDropdownOption, TIMEScreeningState, TrialForDropdown } from '../api/types/timeScreeningStore';

export const formatMatchingTabCounts = (
  counts: TimePrescreeningMatchingTabCountsResponseDtoType,
): Record<MatchingTab, number> => {
  return {
    [MatchingTab.URGENT]: counts.urgent,
    [MatchingTab.UNSCREENED]: counts.unscreened,
    [MatchingTab.RESCREEN]: counts.rescreen,
    [MatchingTab.SCREEN_LATER]: counts.screenLater,
    [MatchingTab.CASE_REVIEW]: counts.caseReview,
  };
};

export const getTrialOpts = (trials: TrialForDropdown[]): DropdownOption[] =>
  trials.map((t) => ({
    label: `${t.nctId || t.tempusTrialId}${t.shortName ? ` - ${t.shortName}` : ''}`,
    value: t.id,
  }));

export const getDropdownOptionsFromIdNameArray = (
  genericIdNameArray: { id: string; name: string }[],
): DropdownOption[] => {
  return genericIdNameArray.map((item) => ({
    label: item.name,
    value: item.id,
  }));
};

export const formatTrialArmSites = (sites: Institution[]): DropdownOption[] => {
  return sites.map((site) => ({
    label: site.name,
    value: site.id,
  }));
};

export const formatTimeLocations = (
  institutions: Array<{
    timeLocationId: string;
    timeLocationName: string;
    timeSiteId: string;
    timeSiteName: string;
    type: string;
  }>,
): LocationDropdownOption[] => {
  const unique = new Map<string, LocationDropdownOption>();

  institutions
    .filter((i) => i.type === InstitutionBehavior.LOCATION)
    .forEach(({ timeLocationId, timeLocationName, timeSiteId }) => {
      if (!unique.has(timeLocationId)) {
        unique.set(timeLocationId, { label: timeLocationName, value: timeLocationId, timeSiteId });
      }
    });

  return [...unique.values()];
};

export interface FilteredLocationOptions {
  options: LocationDropdownOption[];
  // Selected locations narrowed to only those still present in `options`.
  selected: DropdownOption[];
}

export const filterLocationOptionsBySites = (
  locationOptions: LocationDropdownOption[],
  selectedSites: DropdownOption[],
  selectedLocations: DropdownOption[],
): FilteredLocationOptions => {
  if (!selectedSites?.length) {
    return { options: locationOptions, selected: selectedLocations };
  }

  const selectedSiteIds = new Set(selectedSites.map((site) => site.value));
  const options = locationOptions.filter((location) => selectedSiteIds.has(location.timeSiteId));
  const availableLocationIds = new Set(options.map((location) => location.value));
  const selected = selectedLocations.filter((location) => availableLocationIds.has(location.value));

  return { options, selected };
};

export const formatSavedTrialFilters = (
  savedTrialFilters: TimePrescreeningSavedSearchResponseDtoType | null,
): TIMEScreeningState['trials']['filters'] => {
  if (!savedTrialFilters) {
    return defaultTrialFilters;
  }

  const updatedAt =
    savedTrialFilters.updatedAt instanceof Date
      ? savedTrialFilters.updatedAt.toISOString()
      : (savedTrialFilters.updatedAt ?? null);

  return {
    ...defaultTrialFilters,
    lastUpdated: updatedAt,
    locationStatuses: (savedTrialFilters.locationStatus ?? '')
      .split(',')
      .map((val) => locationStatusOpts.find((opt) => opt.value === val))
      .filter((item) => item !== undefined),
    matchScoreMin: (savedTrialFilters.minScore ?? 0).toString(),
    emrEncounterLookback:
      lookbackFilterOpts.find(
        (opt) => parseFloat(opt.value) === (savedTrialFilters.encounterDateLookbackPeriod ?? 0),
      ) || lookbackFilterOpts[lookbackFilterOpts.length - 1],
    tempusReportLookback:
      lookbackFilterOpts.find((opt) => parseFloat(opt.value) === (savedTrialFilters.reportDateLookbackPeriod ?? 0)) ||
      lookbackFilterOpts[lookbackFilterOpts.length - 1],
    upcomingVisitFilterType: savedTrialFilters.upcomingVisitBefore
      ? upcomingVisitFilterTypeOpts[1]
      : upcomingVisitFilterTypeOpts[0],
    upcomingVisitWithin:
      upcomingVisitWithinOpts.find((opt) => parseInt(opt.value) === (savedTrialFilters.upcomingVisitWithin ?? -1)) ||
      upcomingVisitWithinOpts[upcomingVisitWithinOpts.length - 1],
    upcomingVisitBefore:
      savedTrialFilters.upcomingVisitBefore instanceof Date
        ? moment.utc(savedTrialFilters.upcomingVisitBefore).format('MM/DD/YYYY')
        : savedTrialFilters.upcomingVisitBefore
          ? moment
              .utc(savedTrialFilters.upcomingVisitBefore, [moment.ISO_8601, 'YYYY-MM-DD', 'MM/DD/YYYY'], true)
              .format('MM/DD/YYYY')
          : '',
  };
};

const trialMatchingTabToV3StatesMap = {
  [MatchingTab.URGENT]: v3MatchingQueueStates(TimePatientArmEvaluationState.SCREENING_QUEUE_URGENT),
  [MatchingTab.UNSCREENED]: v3MatchingQueueStates(TimePatientArmEvaluationState.SCREENING_QUEUE),
  [MatchingTab.RESCREEN]: v3MatchingQueueStates(TimePatientArmEvaluationState.RESCREEN),
  [MatchingTab.SCREEN_LATER]: v3MatchingQueueStates(TimePatientArmEvaluationState.SNOOZED),
  [MatchingTab.CASE_REVIEW]: v3MatchingQueueStates(TimePatientArmEvaluationState.CASE_REVIEW),
} satisfies Record<MatchingTab, TimePrescreeningMatchingQueueRequestDtoType['states']>;

const carePathwayMatchingTabToStatesMap: Record<Exclude<MatchingTab, MatchingTab.URGENT>, PrescreeningState[]> = {
  [MatchingTab.UNSCREENED]: [PrescreeningStates.UNSCREENED],
  [MatchingTab.RESCREEN]: [PrescreeningStates.RESCREEN],
  [MatchingTab.SCREEN_LATER]: [PrescreeningStates.SCREEN_LATER],
  [MatchingTab.CASE_REVIEW]: [PrescreeningStates.CASE_REVIEW],
};

export const matchingQueueSortOptToSortByMap: Record<string, TimePrescreeningMatchingSortByDtoType> = {
  [TableSortValue.MATCH_SCORE_HIGHEST]: { field: 'matchScore', order: 'DESC' },
  [TableSortValue.MATCH_SCORE_LOWEST]: { field: 'matchScore', order: 'ASC' },
  [TableSortValue.UPCOMING_VISIT_SOONEST]: { field: 'upcomingVisit', order: 'ASC' },
  [TableSortValue.UPCOMING_VISIT_FURTHEST]: { field: 'upcomingVisit', order: 'DESC' },
  [TableSortValue.REPORT_DATE_LATEST]: { field: 'reportDate', order: 'DESC' },
  [TableSortValue.REPORT_DATE_OLDEST]: { field: 'reportDate', order: 'ASC' },
  [TableSortValue.ENCOUNTER_DATE_LATEST]: { field: 'encounterDate', order: 'DESC' },
  [TableSortValue.ENCOUNTER_DATE_OLDEST]: { field: 'encounterDate', order: 'ASC' },
};

export const screenedQueueSortOptToSortByMap: Record<string, TimePrescreeningScreenedSortByDtoType> = {
  [TableSortValue.NEWEST]: { field: 'updatedAt', order: 'DESC' },
  [TableSortValue.OLDEST]: { field: 'updatedAt', order: 'ASC' },
  [TableSortValue.MATCH_SCORE_HIGHEST]: { field: 'matchScore', order: 'DESC' },
  [TableSortValue.MATCH_SCORE_LOWEST]: { field: 'matchScore', order: 'ASC' },
};

export const sortOptToSortByMap: Record<string, { field: string; order: 'ASC' | 'DESC' }> = {
  [TableSortValue.DIAGNOSIS_DATE_LATEST]: {
    field: 'diagnosisDate',
    order: 'DESC',
  },
  [TableSortValue.DIAGNOSIS_DATE_OLDEST]: {
    field: 'diagnosisDate',
    order: 'ASC',
  },
  [TableSortValue.UPCOMING_VISIT_SOONEST]: {
    field: 'upcomingVisit',
    order: 'ASC',
  },
  [TableSortValue.UPCOMING_VISIT_FURTHEST]: {
    field: 'upcomingVisit',
    order: 'DESC',
  },
  [TableSortValue.REPORT_DATE_LATEST]: {
    field: 'reportDate',
    order: 'DESC',
  },
  [TableSortValue.REPORT_DATE_OLDEST]: {
    field: 'reportDate',
    order: 'ASC',
  },
};

export const parseTrialMatchingQueueParams = (
  filters: Partial<TIMEScreeningState['trials']['filters']>,
): {
  states: TimePrescreeningMatchingQueueRequestDtoType['states'];
  parsedFilters: NonNullable<TimePrescreeningMatchingQueueRequestDtoType['filterBy']>;
  sortBy: TimePrescreeningMatchingSortByDtoType;
} => {
  const states = trialMatchingTabToV3StatesMap[filters.tab || MatchingTab.UNSCREENED];
  const isUnscreenedTab = (filters.tab || MatchingTab.UNSCREENED) === MatchingTab.UNSCREENED;
  const parsedFilters: NonNullable<TimePrescreeningMatchingQueueRequestDtoType['filterBy']> = {};
  const {
    upcomingVisitBefore,
    upcomingVisitWithin,
    sites,
    cohort,
    patientLinkageId,
    locations,
    tempusReportLookback,
    locationStatuses,
    matchScoreMin,
    emrEncounterLookback,
    upcomingVisitFilterType,
  } = filters;

  if (isEmpty(filters) || Object.keys(filters).every((key) => key === 'tab')) {
    return {
      states,
      parsedFilters,
      sortBy: matchingQueueSortOptToSortByMap[TableSortValue.MATCH_SCORE_HIGHEST],
    };
  }

  if (cohort?.value) {
    parsedFilters.armIds = [cohort.value];
  }

  if (sites?.length) {
    parsedFilters.siteIds = sites.map((site) => site.value);
  }

  if (patientLinkageId) {
    parsedFilters.patientLinkageId = patientLinkageId;
  }

  if (locations?.length) {
    parsedFilters.locationIds = locations.map((opt) => opt.value);
  }

  // Determine if any trial filters are applied (excluding default values)
  const areTrialFiltersApplied =
    (locationStatuses?.length && locationStatuses.length < locationStatusOpts.length) ||
    (matchScoreMin && !['0', '0.0'].includes(matchScoreMin)) ||
    (emrEncounterLookback?.value && emrEncounterLookback.value !== '0') ||
    (tempusReportLookback?.value && tempusReportLookback.value !== '0') ||
    (upcomingVisitFilterType?.value === WITHIN && upcomingVisitWithin?.value !== '-1') ||
    (upcomingVisitFilterType?.value === BEFORE_SPECIFIED_DATE && !!upcomingVisitBefore);

  // Omitted (not {}) off Unscreened: backend only loads saved search for screening_queue, and {} would skew its tab count.
  if (areTrialFiltersApplied && isUnscreenedTab) {
    parsedFilters.savedTrialFiltersOverride = {};

    if (locationStatuses?.length && locationStatuses.length < locationStatusOpts.length) {
      parsedFilters.savedTrialFiltersOverride.locationStatus = locationStatuses.map((opt) => opt.value).join(',');
    }

    if (matchScoreMin && !['0', '0.0'].includes(matchScoreMin)) {
      parsedFilters.savedTrialFiltersOverride.minScore = parseFloat(matchScoreMin);
    }

    if (emrEncounterLookback?.value && emrEncounterLookback.value !== '0') {
      parsedFilters.savedTrialFiltersOverride.encounterDateLookbackPeriod = parseFloat(emrEncounterLookback.value);
    }

    if (tempusReportLookback?.value && tempusReportLookback.value !== '0') {
      parsedFilters.savedTrialFiltersOverride.reportDateLookbackPeriod = parseFloat(tempusReportLookback.value);
    }

    if (upcomingVisitFilterType?.value === WITHIN && upcomingVisitWithin?.value && upcomingVisitWithin.value !== '-1') {
      parsedFilters.savedTrialFiltersOverride.upcomingVisitWithin = parseInt(upcomingVisitWithin.value);
    } else if (upcomingVisitFilterType?.value === BEFORE_SPECIFIED_DATE && !!upcomingVisitBefore) {
      parsedFilters.savedTrialFiltersOverride.upcomingVisitBefore = moment(
        upcomingVisitBefore,
        ['MM/DD/YYYY', moment.ISO_8601],
        true,
      ).toDate() as unknown as string;
    }
  }

  const sortBy = matchingQueueSortOptToSortByMap[filters.sortBy?.value || TableSortValue.MATCH_SCORE_HIGHEST];

  return { states, parsedFilters, sortBy };
};

export const parseTrialScreenedFilterBy = (
  filters: Partial<TIMEScreeningState['trials']['filters']>,
): TimePrescreeningScreenedRequestDtoType['filterBy'] => {
  const filterBy: NonNullable<TimePrescreeningScreenedRequestDtoType['filterBy']> = {};

  if (filters.locations?.length) {
    filterBy.locationIds = filters.locations.map((opt) => opt.value);
  }

  if (filters.decision?.value) {
    filterBy.currentState = filters.decision.value;
  }

  if (filters.locationStatus?.length) {
    filterBy.locationStatus = filters.locationStatus.map((status) => status.value);
  }

  if (filters.noteText) {
    filterBy.note = filters.noteText;
  }

  if (filters.patientId) {
    filterBy.patientLinkageId = filters.patientId;
  }

  if (filters.trials?.length) {
    filterBy.trialIds = filters.trials.map((trial) => trial.value);
  }

  return filterBy;
};

export const parseCarePathwayMatchingQueueParams = (
  filters: Partial<TIMEScreeningState['careGap']['filters']>,
  isForTabCounts: boolean = false,
): Partial<GetCareGapMatchingQueueReqBody> => {
  const reqBody: Partial<GetCareGapMatchingQueueReqBody> = {
    states: carePathwayMatchingTabToStatesMap[filters.tab || MatchingTab.UNSCREENED],
    page: filters.page || 1,
    pageSize: filters.pageSize || 15,
    sort: sortOptToSortByMap[filters.sortBy?.value || TableSortValue.REPORT_DATE_LATEST] as NonNullable<
      GetCareGapMatchingQueueReqBody['sort']
    >,
  };

  const { stage, sites, patientLinkageId: patientId, location } = filters;

  if (stage?.value) {
    reqBody.stageId = stage.value;
  }

  if (sites?.length) {
    reqBody.siteIds = sites.map((site) => site.value);
  }

  if (patientId) {
    reqBody.patientId = patientId;
  }

  if (location) {
    reqBody.locationName = location;
  }

  return isForTabCounts ? omit(reqBody, ['states', 'page', 'pageSize', 'sort']) : reqBody;
};

export const formatMatchingCareGapTabCounts = (counts: GetCareGapMatchingTabCountsRes): Record<MatchingTab, number> => {
  return {
    [MatchingTab.URGENT]: NaN,
    [MatchingTab.UNSCREENED]: counts[PrescreeningStates.UNSCREENED],
    [MatchingTab.RESCREEN]: counts[PrescreeningStates.RESCREEN],
    [MatchingTab.SCREEN_LATER]: counts[PrescreeningStates.SCREEN_LATER],
    [MatchingTab.CASE_REVIEW]: counts[PrescreeningStates.CASE_REVIEW],
  };
};
